import React, { useCallback } from 'react';

import { useIntl } from 'react-intl';

import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types/next-editor-plugin';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import { ToolbarDropdownItem } from '@atlaskit/editor-toolbar/toolbar-dropdown-item';

import type { AIPlugin } from '../../aiPluginType';
import { createOpenAIModalCommand } from '../../pm-plugins/decoration/actions';
import type { EditorPluginAIProvider } from '../../types/types';
import type { BlockMenuActionDef } from './types';

/**
 * Resolves the click handler for a block menu action.
 *
 * - Config item actions open the AI modal via `createOpenAIModalCommand`.
 */

const executeAction = ({
	action,
	editorView,
	editorPluginAIProvider,
	locale,
}: {
	action: BlockMenuActionDef;
	editorPluginAIProvider: EditorPluginAIProvider;
	editorView: EditorView | undefined;
	locale: string;
}): boolean => {
	// Custom click actions — invoke the handler directly
	if (action.onClick) {
		action.onClick();
		return true;
	}

	// Config item actions — open the AI modal
	if (editorView && action.configItem) {
		const openAIModalCommand = createOpenAIModalCommand({
			state: editorView.state,
			configItem: action.configItem,
			lastTriggeredFrom: 'blockMenu',
			triggerMethod: 'click',
			aiGlobalOptIn: editorPluginAIProvider.aiGlobalOptIn,
			locale,
		});

		openAIModalCommand(editorView.state, editorView.dispatch);
		return true;
	}

	return false;
};

export const BlockMenuActionItem = ({
	action,
	api,
	editorPluginAIProvider,
	getEditorView,
}: {
	action: BlockMenuActionDef;
	api: ExtractInjectionAPI<AIPlugin>;
	editorPluginAIProvider: EditorPluginAIProvider;
	getEditorView: () => EditorView | undefined;
}): React.JSX.Element => {
	const { formatMessage, locale } = useIntl();

	const handleClick = useCallback(() => {
		// Without stopPreservingSelection, the selection preservation plugin will
		// restore the block selection after clearBlockSelection removes it.
		// clearBlockSelection removes the background highlight decoration
		// (ak-editor-selected-node) without affecting the ProseMirror node selection.
		api?.core?.actions.execute(({ tr }) => {
			api?.blockControls?.commands?.stopPreservingSelection()({ tr });
			api?.blockControls?.commands?.toggleBlockMenu({ closeMenu: true })({ tr });
			api?.selection?.commands?.clearBlockSelection()({ tr });
			return tr;
		});

		executeAction({
			action,
			editorView: getEditorView(),
			editorPluginAIProvider,
			locale,
		});
	}, [action, api, editorPluginAIProvider, getEditorView, locale]);

	return (
		<ToolbarDropdownItem elemBefore={action.elemBefore} onClick={handleClick}>
			{formatMessage(action.label)}
		</ToolbarDropdownItem>
	);
};
